import { hasProp } from '../../utils';

import { MenuProps, MenuObjectProps, MenuItemProps, MenuGroupProps } from './Menu.types';

const helpers = {
  isItem(item: MenuItemProps | MenuGroupProps): item is MenuItemProps {
    return hasProp(item, 'primary');
  },

  getItem(items: MenuProps['items'], id: MenuObjectProps['id']): MenuItemProps | undefined {
    let found: MenuItemProps | undefined;
    items.some(item => {
      if (this.isItem(item) && item.id === id) {
        found = item;
        return true;
      }

      if (item.items) {
        found = this.getItem(item.items, id);
        return !!found;
      }

      return false;
    });

    return found;
  },

  getPath(items: MenuProps['items'], id: MenuObjectProps['id']): MenuProps['items'] {
    let path: (MenuItemProps | MenuGroupProps)[] = [];
    items.some(item => {
      if (item.id === id) {
        path = [item];
        return true;
      }

      if (item.items) {
        const innerPath = this.getPath(item.items, id);
        if (innerPath.length) path = innerPath.concat(item);
        return !!path.length;
      }

      return false;
    });

    return path;
  },

  setItem(
    items: MenuProps['items'],
    id: MenuItemProps['id'],
    newItem: MenuItemProps
  ): MenuProps['items'] {
    return items.map((item: MenuItemProps | MenuGroupProps) => {
      if (item.id === id) return { ...newItem };

      if (item.items) {
        return {
          ...item,
          items: this.setItem(item.items, id, newItem) as MenuItemProps[]
        };
      }

      return item;
    });
  },

  mapItem(
    items: MenuProps['items'],
    id: MenuItemProps['id'],
    fn: (item: MenuItemProps, index: number, array: MenuProps['items']) => MenuItemProps
  ): MenuProps['items'] {
    return items.map((item, index, array) => {
      let newItem = item;

      if (item.items) {
        newItem = {
          ...newItem,
          items: this.mapItem(item.items, id, fn) as MenuItemProps[]
        };
      }

      if (this.isItem(newItem) && item.id === id) {
        newItem = fn(newItem, index, array);
      }

      return newItem;
    });
  },

  mapTree(
    items: MenuProps['items'],
    fn: (item: MenuItemProps, index: number, array: MenuProps['items']) => MenuItemProps
  ): MenuProps['items'] {
    return items.map((item, index, array) => {
      let newItem = item;

      if (item.items) {
        newItem = {
          ...newItem,
          items: this.mapTree(item.items, fn) as MenuItemProps[]
        };
      }

      return this.isItem(newItem) ? fn(newItem, index, array) : newItem;
    });
  },

  flatten(items: MenuProps['items'], ancestors?: MenuProps['items']): MenuItemProps[] {
    return items.reduce<MenuItemProps[]>((flatItems, item) => {
      if (item.items) {
        return this.isItem(item)
          ? [
              ...flatItems,
              item,
              ...this.flatten(item.items, ancestors ? [...ancestors, item] : [item])
            ]
          : [...flatItems, ...this.flatten(item.items, ancestors ? [...ancestors, item] : [item])];
      }
      return this.isItem(item)
        ? [...flatItems, ancestors ? { ...item, ancestors } : item]
        : flatItems;
    }, []);
  },

  toggleSelected(
    items: MenuProps['items'],
    id: MenuItemProps['id'],
    mode?: MenuProps['mode'],
    bool?: boolean
  ): MenuProps['items'] {
    return this.mapTree(items, item => {
      if (this.isItem(item)) {
        if (item.id === id) {
          return {
            ...item,
            selected: bool !== undefined ? bool : !item.selected
          };
        }

        if (mode === 'single-select') {
          return {
            ...item,
            selected: false
          };
        }
      }

      return item;
    });
  },

  selectItem(
    items: MenuProps['items'],
    id: MenuItemProps['id'],
    mode?: MenuProps['mode']
  ): MenuProps['items'] {
    return this.toggleSelected(items, id, mode, true);
  },

  deselectItem(
    items: MenuProps['items'],
    id: MenuItemProps['id'],
    mode?: MenuProps['mode']
  ): MenuProps['items'] {
    return this.toggleSelected(items, id, mode, false);
  },

  getSelected(items: MenuProps['items']): MenuItemProps[] {
    return items.reduce<MenuItemProps[]>((selections, item) => {
      if (this.isItem(item) && item.selected) {
        selections = [...selections, item];
      }

      if (item.items) {
        selections = [...selections, ...this.getSelected(item.items)];
      }
      return selections;
    }, []);
  },

  prependTo(
    items: MenuProps['items'],
    newItems: MenuProps['items'],
    id?: MenuObjectProps['id']
  ): MenuProps['items'] {
    if (id) {
      return this.mapItem(items, id, item => ({
        ...item,
        items: [...newItems, ...(item.items ?? [])]
      }));
    }
    return [...newItems, ...items];
  },

  appendTo(
    items: MenuProps['items'],
    newItems: MenuProps['items'],
    id?: MenuObjectProps['id']
  ): MenuProps['items'] {
    if (id) {
      return this.mapItem(items, id, item => ({
        ...item,
        items: [...(item.items ?? []), ...newItems]
      }));
    }
    return [...items, ...newItems];
  },

  getNextItem(
    items: MenuProps['items'],
    itemId?: MenuObjectProps['id']
  ): MenuItemProps | MenuGroupProps | undefined {
    if (!itemId) return items[0];
    let nextItem: MenuItemProps | MenuGroupProps | undefined;
    this.mapItem(items, itemId, (item, index, list) => {
      nextItem = list[index + 1];
      return item;
    });
    return nextItem;
  },

  getPrevItem(
    items: MenuProps['items'],
    itemId?: MenuObjectProps['id']
  ): MenuItemProps | MenuGroupProps | undefined {
    if (!itemId) return items[0];
    let prevItem: MenuItemProps | MenuGroupProps | undefined;
    this.mapItem(items, itemId, (item, index, list) => {
      prevItem = list[index - 1];
      return item;
    });
    return prevItem;
  },

  getParentItem(
    items: MenuProps['items'],
    itemId?: MenuItemProps['id']
  ): MenuItemProps | MenuGroupProps | undefined {
    if (!itemId) return undefined;
    const [, parentItem] = this.getPath(items, itemId);
    return parentItem;
  }
};

export default helpers;
